import React from "react";
import { Helmet } from "react-helmet-async";
import { Box, Stack, Typography, TextField } from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";

import useBM from "../../../hooks/super/useBM";
import useAuth from "../../../hooks/useAuth";

import AwsS3CfgHistoryList from "../../../components/pages/application/bot/T_AwsS3CfgHistory";

import { CollapseAlert, LoadingButton, SnackbarAlert } from "../../../components/pages/common/styled";
import { UserType } from "../../../utils/constants";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import {
  TOKENS,
  SectionCard,
} from "../../../components/pages/_new/super/design";

function SAAwsS3Cfgs() {
  const { awsS3Cfgs, getAwsS3CfgHistory, insertAwsS3Cfg, applyAwsS3Cfg, errMsg, setErr } = useBM();
  const { isAuthenticated, adminUserType } = useAuth();

  const [snackOpen, setSnackOpen] = React.useState(false);
  const handleSnackClose = () => setSnackOpen(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const [loading, setLoading] = React.useState(false);

  const awsAccessKeyIdRef = React.useRef(null);
  const [awsAccessKeyIdValue, setAwsAccessKeyIdValue] = React.useState("");
  const awsSecretAccessKeyIdRef = React.useRef(null);
  const [awsSecretAccessKeyIdValue, setAwsSecretAccessKeyIdValue] = React.useState("");
  const awsStorageBucketNameRef = React.useRef(null);
  const [awsStorageBucketNameValue, setAwsStorageBucketNameValue] = React.useState("");
  const awsS3RegionNameRef = React.useRef(null);
  const [awsS3RegionNameValue, setAwsS3RegionNameValue] = React.useState("");

  const saveAwsS3Cfg = () => {
    if (!awsAccessKeyIdValue) {
      awsAccessKeyIdRef.current?.focus();
      return;
    }
    if (!awsSecretAccessKeyIdValue) {
      awsSecretAccessKeyIdRef.current?.focus();
      return;
    }
    if (!awsStorageBucketNameValue) {
      awsStorageBucketNameRef.current?.focus();
      return;
    }
    if (!awsS3RegionNameValue) {
      awsS3RegionNameRef.current?.focus();
      return;
    }
    insertAwsS3Cfg(awsAccessKeyIdValue, awsSecretAccessKeyIdValue, awsStorageBucketNameValue, awsS3RegionNameValue);
  };
  const changeAwsAccessKeyId = (e) => setAwsAccessKeyIdValue(e.target.value);
  const changeAwsSecretAccessKeyId = (e) => setAwsSecretAccessKeyIdValue(e.target.value);
  const changeAwsStorageBucketName = (e) => setAwsStorageBucketNameValue(e.target.value);
  const changeAwsS3RegionName = (e) => setAwsS3RegionNameValue(e.target.value);

  React.useEffect(() => {
    if (isAuthenticated) getAwsS3CfgHistory(0, 0);
    return () => setErr(null);
  }, [isAuthenticated, setErr, getAwsS3CfgHistory]);

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  const apply = async () => {
    if (awsS3Cfgs === null) {
      setErr("You must upload or generate your own configuration");
      return;
    }
    setLoading(true);
    const result = await applyAwsS3Cfg();
    setMessage(result.msg);
    setSuccess(result.status);
    setLoading(false);
    setSnackOpen(true);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${TOKENS.radiusXs}px`,
      background: TOKENS.bgSurface,
    },
  };
  const labelSx = { fontSize: 12.5, color: TOKENS.fg2, mb: 0.75, fontWeight: 500 };
  const canEdit = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);
  const isSuper = UserType.SUPER_ADMIN === adminUserType;

  return (
    <React.Fragment>
      <Helmet title="SA AWS S3 Configuration" />
      <PageHeader
        title="AWS S3 Configuration"
        description="S3 bucket where Bot Management archived events are written"
        actions={
          <LoadingButton
            variant="contained"
            startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
            loadingPosition="start"
            loading={loading}
            onClick={apply}
            disabled={!canEdit}
            sx={{
              height: 36,
              borderRadius: "8px",
              backgroundColor: "#6371F7",
              fontSize: 14,
              fontWeight: 400,
              textTransform: "none",
              "&:hover": { backgroundColor: "#4959ee" },
            }}
          >
            Apply
          </LoadingButton>
        }
      />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Stack spacing={2}>
        <SectionCard title="AWS credentials" description="IAM access key + secret access key">
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>AWS_ACCESS_KEY_ID</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="AKIA..."
                required
                value={awsAccessKeyIdValue}
                onChange={changeAwsAccessKeyId}
                inputRef={awsAccessKeyIdRef}
                sx={inputSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>AWS_SECRET_ACCESS_KEY</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Secret access key"
                required
                value={awsSecretAccessKeyIdValue}
                onChange={changeAwsSecretAccessKeyId}
                inputRef={awsSecretAccessKeyIdRef}
                sx={inputSx}
              />
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard title="Bucket location" description="Target bucket + region">
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>AWS_STORAGE_BUCKET_NAME</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="layerd-bm-events"
                required
                value={awsStorageBucketNameValue}
                onChange={changeAwsStorageBucketName}
                inputRef={awsStorageBucketNameRef}
                sx={inputSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>AWS_S3_REGION_NAME</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="us-east-1"
                required
                value={awsS3RegionNameValue}
                onChange={changeAwsS3RegionName}
                inputRef={awsS3RegionNameRef}
                sx={inputSx}
              />
            </Box>
            <Box>
              <PrimaryButton
                onClick={saveAwsS3Cfg}
                startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
                disabled={!isSuper}
              >
                Save
              </PrimaryButton>
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard title="History" description="Previous AWS S3 configurations">
          <AwsS3CfgHistoryList />
        </SectionCard>
      </Stack>
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default SAAwsS3Cfgs;
